import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  MessageSquareQuote,
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  ArrowLeft,
  Mail,
  FileText,
  Star,
  Send,
  Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmailComposer from "./EmailComposer";

export default function TestimonialFlow() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Generate outreach, 2: Paste reply, 3: Polish & Case Study
  
  // Form state
  const [tone, setTone] = useState("friendly");
  const [outreach, setOutreach] = useState("");
  const [clientReply, setClientReply] = useState("");
  const [polishedQuote, setPolishedQuote] = useState("");
  const [caseStudyMd, setCaseStudyMd] = useState("");
  const [currentTestimonialId, setCurrentTestimonialId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailContent, setEmailContent] = useState("");

  useEffect(() => {
    fetchProject();
    fetchTestimonials();
    fetchCaseStudies();
    fetchClients();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProject(res.data);
      // Fetch the client for this project
      if (res.data.client_id) {
        const clientRes = await api.get(`/clients/${res.data.client_id}`);
        setClient(clientRes.data);
      }
    } catch (e) {
      console.error("Error fetching project:", e);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data);
    } catch (e) {
      console.error("Error fetching clients:", e);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const res = await api.get(`/testimonials?project_id=${projectId}`);
      setTestimonials(res.data.items || []);
    } catch (e) {
      console.error("Error fetching testimonials:", e);
    }
  };

  const fetchCaseStudies = async () => {
    try {
      const res = await api.get(`/case-studies?project_id=${projectId}`);
      setCaseStudies(res.data.items || []);
    } catch (e) {
      console.error("Error fetching case studies:", e);
    }
  };

  const handleGenerateOutreach = async () => {
    setLoading(true);
    try {
      const res = await api.post("/testimonials/outreach", {
        project_id: projectId,
        tone
      });
      
      setOutreach(res.data.outreach_email || "");
      setCurrentTestimonialId(res.data.testimonial_id);
      setStep(2);
      toast.success("Outreach email generated!");
      fetchTestimonials();
    } catch (e) {
      console.error("Generate error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate outreach");
    } finally {
      setLoading(false);
    }
  };

  const handlePolishQuote = async () => {
    if (!currentTestimonialId || !clientReply.trim()) {
      toast.error("Please paste the client's reply first");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post(`/testimonials/${currentTestimonialId}/ingest-reply`, {
        raw_text: clientReply
      });
      
      setPolishedQuote(res.data.polished_quote || "");
      setStep(3);
      toast.success("Quote polished!");
      fetchTestimonials();
    } catch (e) {
      console.error("Polish error:", e);
      toast.error(e.response?.data?.detail || "Failed to polish quote");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCaseStudy = async (style = "long") => {
    if (!currentTestimonialId) return;
    
    setLoading(true);
    try {
      const res = await api.post(`/case-studies/from-testimonial/${currentTestimonialId}`, {
        style: style,
        title: `Case Study — ${project?.title || 'Project'}`
      });
      
      setCaseStudyMd(res.data.body_md || "");
      toast.success("Case study generated!");
      fetchCaseStudies();
    } catch (e) {
      console.error("Case study error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate case study");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCaseStudy = () => {
    if (!caseStudyMd) return;
    const blob = new Blob([caseStudyMd], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Case_Study_${project?.title || 'Project'}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Case study exported!");
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const loadExistingTestimonial = (t) => {
    setCurrentTestimonialId(t.id);
    setOutreach(t.outreach_email || "");
    setClientReply(t.client_reply_raw || "");
    setPolishedQuote(t.polished_quote || "");
    
    if (t.polished_quote) setStep(3);
    else if (t.client_reply_raw) setStep(3);
    else setStep(2);
  };

  return (
    <div className="space-y-6" data-testid="testimonial-flow">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/projects"
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-indigo-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Testimonials & Case Studies</h1>
            <p className="text-indigo-400">
              {project?.title || "Project"} — Collect and showcase client feedback
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-green-500/20 text-green-400">
            {testimonials.length} testimonial{testimonials.length !== 1 ? "s" : ""}
          </Badge>
          <Badge className="bg-indigo-500/20 text-indigo-400">
            {caseStudies.length} case stud{caseStudies.length !== 1 ? "ies" : "y"}
          </Badge>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`p-4 rounded-xl border transition-colors ${
              step === s
                ? "bg-indigo-500/20 border-indigo-500/50"
                : step > s
                ? "bg-green-500/10 border-green-500/30"
                : "bg-slate-800/30 border-slate-700/50"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step > s ? "bg-green-500 text-white" : step === s ? "bg-indigo-500 text-white" : "bg-slate-400 text-indigo-400"
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              <span className={`text-sm font-medium ${step >= s ? "text-indigo-500" : "text-slate-400"}`}>
                {s === 1 ? "Generate Outreach" : s === 2 ? "Collect Reply" : "Polish & Build"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Step 1: Generate Outreach */}
      {step >= 1 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-400" />
              Outreach Email
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Generate a friendly email to request a testimonial from your client
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="friendly" className="text-slate-400">Friendly</SelectItem>
                  <SelectItem value="formal" className="text-slate-400">Formal</SelectItem>
                  <SelectItem value="casual" className="text-slate-400">Casual</SelectItem>
                </SelectContent>
              </Select>
              
              <button
                onClick={handleGenerateOutreach}
                disabled={loading}
                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-600 text-white px-4 py-2 rounded-xl transition-colors"
                data-testid="generate-outreach-btn"
              >
                {loading && step === 1 ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate Outreach
              </button>
            </div>
            
            {outreach && (
              <div className="relative">
                <Textarea
                  value={outreach}
                  onChange={(e) => setOutreach(e.target.value)}
                  className="min-h-[200px] bg-slate-900/50 border-slate-600 text-slate-600"
                />
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => {
                      setEmailContent(`Subject: Request for Testimonial - ${project?.title || 'Project'}\n\n${outreach}`);
                      setShowEmailComposer(true);
                    }}
                    className="p-2 bg-green-600 hover:bg-green-500 rounded-lg"
                    title="Send via email"
                    data-testid="send-outreach-email-btn"
                  >
                    <Send className="w-4 h-4 text-indigo-500" />
                  </button>
                  <button
                    onClick={() => handleCopy(outreach)}
                    className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-indigo-400" />}
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Paste Client Reply */}
      {step >= 2 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <MessageSquareQuote className="w-5 h-5 text-green-400" />
              Client Reply
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Paste your client's testimonial response here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={clientReply}
              onChange={(e) => setClientReply(e.target.value)}
              placeholder="Paste your client's testimonial reply here..."
              className="min-h-[150px] bg-slate-900/50 border-slate-600 text-slate-600"
              data-testid="client-reply-input"
            />
            
            <button
              onClick={handlePolishQuote}
              disabled={loading || !clientReply.trim()}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:bg-slate-600 text-indigo-500 px-4 py-2 rounded-xl transition-colors"
              data-testid="polish-quote-btn"
            >
              {loading && step === 2 ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Star className="w-4 h-4" />
              )}
              Polish Quote
            </button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Polished Quote & Case Study */}
      {step >= 3 && polishedQuote && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <Star className="w-5 h-5 text-indigo-400" />
              Polished Testimonial
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative p-4 bg-gradient-to-r from-indigo-500/10 to-orange-500/10 rounded-xl border border-indigo-500/30">
              <p className="text-lg text-slate-600 italic">"{polishedQuote}"</p>
              <button
                onClick={() => handleCopy(polishedQuote)}
                className="absolute top-2 right-2 p-2 bg-slate-400 hover:bg-slate-600 rounded-lg"
              >
                <Copy className="w-4 h-4 text-indigo-400" />
              </button>
            </div>
            
            {/* Case Study Generation Options */}
            <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50">
              <h4 className="text-sm font-medium text-slate-400 mb-3">Generate Case Study</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCreateCaseStudy("short")}
                  disabled={loading}
                  className="flex items-center gap-2 bg-indigo-500/20 hover:bg-slate-400/30 disabled:bg-slate-600 text-indigo-400 px-4 py-2 rounded-lg transition-colors text-sm"
                  data-testid="create-case-study-short-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Short (1 page)
                </button>
                <button
                  onClick={() => handleCreateCaseStudy("long")}
                  disabled={loading}
                  className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  data-testid="create-case-study-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Detailed (2-3 pages)
                </button>
              </div>
            </div>
            
            {caseStudyMd && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-400">Generated Case Study</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopy(caseStudyMd)}
                      className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-indigo-400" />}
                    </button>
                    <button
                      onClick={handleExportCaseStudy}
                      className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Export as Markdown"
                    >
                      <Download className="w-4 h-4 text-indigo-400" />
                    </button>
                  </div>
                </div>
                <Textarea
                  value={caseStudyMd}
                  onChange={(e) => setCaseStudyMd(e.target.value)}
                  className="min-h-[300px] font-mono text-sm bg-slate-900/50 border-slate-600 text-slate-600"
                  data-testid="case-study-editor"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Existing Testimonials */}
      {testimonials.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 text-sm">Saved Testimonials</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {testimonials.map((t) => (
                <button
                  key={t.id}
                  onClick={() => loadExistingTestimonial(t)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    currentTestimonialId === t.id
                      ? "bg-indigo-500/20 border border-indigo-500/50"
                      : "bg-slate-900/30 hover:bg-slate-900/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 truncate">
                      {t.polished_quote || t.client_reply_raw || "Outreach sent..."}
                    </span>
                    <Badge className={`text-xs ${
                      t.status === "polished" ? "bg-green-500/20 text-green-400" :
                      t.status === "received" ? "bg-blue-500/20 text-indigo-400" :
                      "bg-slate-400 text-indigo-400"
                    }`}>
                      {t.status}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={emailContent}
        clients={client ? [client, ...clients.filter(c => c.id !== client.id)] : clients}
      />
    </div>
  );
}
